/**
 * [INPUT]: 依赖 ../scorer 的 scoreFromLandmarks/类型，依赖本目录 preprocess/mediapipe/pose/rejects/config
 * [OUTPUT]: 对外提供 scoreImage(file, opts) → ScoreReport | RejectResult；端侧评分主入口
 * [POS]: apps/web/pipeline 的编排层，串联 docs §2.3 流水线：预处理→检测→姿态→去滚转→评分；原图不离浏览器
 * [PROTOCOL]: 变更时更新此头部，然后检查 ../../CLAUDE.md
 */
import {
  scoreFromLandmarks,
  derollLandmarks,
  type Gender,
  type GenderSource,
  type Landmark,
  type RejectResult,
  type ScoreReport,
} from '../scorer'
import { PIPELINE_CONFIG } from './config'
import { loadAndPreprocess, validateFile, type PreprocessedImage } from './preprocess'
import { getLandmarker } from './mediapipe'
import { composePose } from './pose'
import { reject } from './rejects'

export type { ScoreReport, RejectResult }

export interface ScoreOptions {
  gender: Gender
  genderSource: GenderSource
  createdAt?: string
}

export type PipelineResult = ScoreReport | RejectResult

export async function scoreImage(file: File, opts: ScoreOptions): Promise<PipelineResult> {
  // 1. 文件类型/大小校验
  const fileErr = validateFile(file)
  if (fileErr) return reject(fileErr)

  // 2. 预处理（解码 + EXIF + 缩放 + 质量估计）
  let pre: PreprocessedImage
  try {
    pre = await loadAndPreprocess(file)
  } catch {
    return reject('INVALID_FILE')
  }

  // 3. 质量拒绝：模糊 / 光照
  const q = pre.quality
  if (q.blurLaplacianVar < PIPELINE_CONFIG.quality.blurLaplacianVar) return reject('BLUR')
  if (q.brightness < PIPELINE_CONFIG.quality.brightnessMin || q.brightness > PIPELINE_CONFIG.quality.brightnessMax) {
    return reject('LIGHTING')
  }

  // 4. 关键点检测（FaceLandmarker 同时承担检测）
  const landmarker = await getLandmarker()
  const result = landmarker.detect(pre.canvas)
  const faces = result.faceLandmarks ?? []
  if (faces.length === 0) return reject('NO_FACE')
  if (faces.length > PIPELINE_CONFIG.face.maxFaces) return reject('MULTI_FACE')
  const norm = faces[0]

  // 5. 人脸大小（关键点横向跨度近似框宽比）
  let minX = Infinity
  let maxX = -Infinity
  for (const p of norm) {
    if (p.x < minX) minX = p.x
    if (p.x > maxX) maxX = p.x
  }
  const faceWidthRatio = maxX - minX
  console.log('[Pipeline] Detected face width ratio:', faceWidthRatio, 'Threshold:', PIPELINE_CONFIG.face.minWidthRatio)
  if (faceWidthRatio < PIPELINE_CONFIG.face.minWidthRatio) return reject('FACE_TOO_SMALL')

  // 6. 像素化关键点（供 scorer 几何）+ 姿态估计 + 姿态拒绝
  const pxLandmarks: Landmark[] = norm.map((p) => ({
    x: p.x * pre.width,
    y: p.y * pre.height,
    z: p.z,
    visibility: p.visibility,
  }))
  const matrix = result.facialTransformationMatrixes?.[0]?.data ?? null
  // pose：roll 用关键点，yaw/pitch 用矩阵（solvePnP）；pxLandmarks 供 scorer 几何
  const pose = composePose(pxLandmarks, matrix)
  console.log('[Pipeline] pose yaw/pitch/roll:', pose.yaw.toFixed(1), pose.pitch.toFixed(1), pose.roll.toFixed(1))
  const t = PIPELINE_CONFIG.pose
  if (Math.abs(pose.yaw) > t.yaw) return reject('POSE_YAW')
  if (Math.abs(pose.pitch) > t.pitch) return reject('POSE_PITCH')
  if (Math.abs(pose.roll) > t.roll) return reject('POSE_ROLL')

  // TODO(OCCLUSION): 关键点可见性/口罩启发式；P0 暂不拒绝，避免误杀

  // 6.1 去滚转：绕瞳孔中点旋转 -roll 使双眼连线水平，消除 roll 对 canthal_tilt / symmetry 的
  //     污染（同脸多照漂移主因：canthal_tilt ≈ 真值 + roll）。仅修评分用关键点，与 canthal_tilt
  //     测量同在 px 空间一致；pose 用真实姿态（拒绝/置信反映真实偏转）。roll≈0 时恒等。
  const scoreLandmarks = derollLandmarks(pxLandmarks, pose.roll)

  // 7. 评分（纯规则）
  return scoreFromLandmarks({
    landmarks: scoreLandmarks,
    gender: opts.gender,
    genderSource: opts.genderSource,
    pose,
    photoQuality: pre.quality.score,
    createdAt: opts.createdAt ?? new Date().toISOString(),
  })
}